// The matcher's left pane: what the scheme remitted, line by line.
//
// One row per ERA line. Clicking an unmatched line arms it, which is the
// interaction the whole screen is built on: arm a source, then apply it to
// targets on the right. Click-to-arm rather than drag, so it works identically
// by mouse, touch and keyboard.
//
// Dismissed lines are hidden behind a toggle rather than struck through in
// place, so what remains is a real to-do list. The toggle carries a count, so
// the dismissed pile is collapsed rather than invisible.

import React from "react";
import { Badge, Button, Form, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { zar } from "../Accounts/lineAllocation";
import { eraLineId } from "./useEraMatchModel";

const fmtDate = (raw) => {
  if (!raw) return "";
  const s = String(raw);
  const iso = /^\d{8}$/.test(s)
    ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
    : s;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString("en-GB");
};

/** Badge for a line's own state. Auto and manual are told apart deliberately. */
function LineBadge({ line }) {
  if (line.status === "matched") {
    return line.matchSource === "manual" ? (
      <Badge bg="success">
        <i className="fa-solid fa-user-check me-1" />
        Matched manually
      </Badge>
    ) : (
      <Badge bg="secondary">Matched automatically</Badge>
    );
  }
  if (line.status === "dismissed") {
    return (
      <Badge bg="dark">
        <i className="fa-solid fa-eye-slash me-1" />
        Dismissed
      </Badge>
    );
  }
  if (Number(line.paidAmount) < 0) {
    return (
      <Badge bg="danger">
        <i className="fa-solid fa-rotate-left me-1" />
        Reversal
      </Badge>
    );
  }
  if (!(line.paidAmount > 0)) {
    return <Badge bg="light" text="dark">Not paid</Badge>;
  }
  return <Badge bg="secondary">To do</Badge>;
}

export default function EraRemittancePane({
  claims,
  armed,
  stagedIds,
  dismissSelection,
  showDismissed,
  dismissedCount,
  showMatched,
  matchedCount,
  outstandingCount,
  onArm,
  onToggleDismissSelect,
  onDismissClaim,
  onOpenEraDetail,
  onToggleShowDismissed,
  onToggleShowMatched,
  onUnmatch,
  onUndismiss,
}) {
  const isArmed = (claim, line) =>
    armed &&
    String(armed.eraLogId) === String(claim.eraLogId) &&
    String(armed.schemeClaimId) === String(claim.schemeClaimId) &&
    String(armed.eraLineKey) === String(line.key);

  const isStaged = (claim, line) =>
    !!stagedIds?.has(`${claim.eraLogId}:${claim.schemeClaimId}:${line.key}`);

  const rowClass = (claim, line) => {
    if (dismissSelection[eraLineId(claim, line)]) {
      return "era-match-row era-match-dismiss-selected";
    }
    if (isStaged(claim, line)) return "era-match-row era-match-applied";
    if (isArmed(claim, line)) return "era-match-row era-match-armed";
    if (line.status === "matched") {
      return `era-match-row ${
        line.matchSource === "manual" ? "era-match-manual" : "era-match-auto"
      }`;
    }
    if (line.status === "dismissed") return "era-match-row era-match-dismissed";
    return "era-match-row";
  };

  // A line is actionable while it is unmatched, not a clawback (negative),
  // and not already confirmed into the receipt being built. Zero-paid ("Not
  // paid") lines arm with a R0.00 budget so they can be mapped to the invoice
  // line they belong to, with the remainder resolved as a shortfall.
  const isActionable = (line, claim) =>
    line.status === "unmatched" &&
    Number(line.paidAmount) >= 0 &&
    !isStaged(claim, line);

  // Any unmatched line can be dismissed, clawbacks included: dismissing is
  // how a reversal nobody will action comes off the list.
  const canDismiss = (line, claim) =>
    line.status === "unmatched" && !isStaged(claim, line);

  // Matched lines (auto or manual) are history, not work; both they and
  // dismissed lines hide behind their toggles so the list reads as a to-do.
  const visibleLines = (claim) =>
    (claim.lines || []).filter(
      (l) =>
        (showDismissed || l.status !== "dismissed") &&
        (showMatched || l.status !== "matched")
    );

  const activate = (claim, line) => (e) => {
    if (e.type === "keydown" && !["Enter", " "].includes(e.key)) return;
    if (e.type === "keydown") e.preventDefault();
    if (!isActionable(line)) return;
    // The row itself is the fly-animation source, so the selection visibly
    // leaves the place the user clicked.
    onArm(claim, line, e.currentTarget);
  };

  const rowsFor = (claim) =>
    visibleLines(claim).map((line) => {
      const id = eraLineId(claim, line);
      const actionable = isActionable(line, claim);
      const dismissable = canDismiss(line, claim);
      return (
        <tr
          key={id}
          className={rowClass(claim, line)}
          role={actionable ? "button" : undefined}
          tabIndex={actionable ? 0 : undefined}
          aria-pressed={actionable ? !!isArmed(claim, line) : undefined}
          onClick={activate(claim, line)}
          onKeyDown={activate(claim, line)}
        >
          <td
            className="text-center align-middle era-match-cell-plain"
            onClick={(e) => e.stopPropagation()}
          >
            {isStaged(claim, line) && (
              <Badge bg="primary">
                <i className="fa-solid fa-check me-1" />
                Confirmed
              </Badge>
            )}
            {actionable &&
              (isArmed(claim, line) ? (
                <Button
                  size="sm"
                  variant="light"
                  className="text-primary fw-semibold"
                  style={{ minWidth: 84 }}
                  onClick={(e) => onArm(claim, line, e.currentTarget.closest("tr"))}
                >
                  <i className="fa-solid fa-xmark me-2" />
                  Matching
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="outline-primary"
                  style={{ minWidth: 84 }}
                  disabled={!!dismissSelection[id]}
                  onClick={(e) => onArm(claim, line, e.currentTarget.closest("tr"))}
                >
                  Match
                </Button>
              ))}
          </td>
          <td className="align-middle">{line.tariffCode || "-"}</td>
          <td className="align-middle">{fmtDate(line.dateOfService)}</td>
          <td className="acc-list-num align-middle">
            {zar.format(line.claimedAmount)}
          </td>
          <td className="acc-list-num align-middle fw-semibold">
            {zar.format(line.paidAmount)}
          </td>
          <td className="align-middle era-match-cell-plain">
            <LineBadge line={line} />
            {line.reasonCodes?.length > 0 && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {line.reasonCodes
                      .map((rc) => `${rc.code}: ${rc.description}`)
                      .join("; ")}
                  </Tooltip>
                }
              >
                <i className="fa-solid fa-circle-info ms-2" />
              </OverlayTrigger>
            )}
          </td>
          <td
            className="text-center align-middle era-match-cell-plain"
            onClick={(e) => e.stopPropagation()}
          >
            {dismissable && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {dismissSelection[id]
                      ? "Keep this line on the list"
                      : "Dismiss: take this line off the list without paying it"}
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  className={`btn btn-link text-decoration-none p-0 border-0 ${
                    dismissSelection[id] ? "text-danger" : "text-secondary"
                  }`}
                  style={{ width: 24, height: 24 }}
                  aria-label={`Dismiss line ${line.tariffCode}`}
                  aria-pressed={!!dismissSelection[id]}
                  onClick={() => onToggleDismissSelect(claim, line)}
                >
                  <i className="fa-solid fa-eye-slash" />
                </button>
              </OverlayTrigger>
            )}
            {line.status === "matched" && line.matchId && onUnmatch && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Un-match</Tooltip>}>
                <button
                  type="button"
                  className="btn btn-link text-primary text-decoration-none p-0 border-0"
                  style={{ width: 24, height: 24 }}
                  aria-label="Un-match this line"
                  onClick={() => onUnmatch(line)}
                >
                  <i className="fa-solid fa-rotate-left" />
                </button>
              </OverlayTrigger>
            )}
            {line.status === "dismissed" && onUndismiss && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Undo dismiss</Tooltip>}>
                <button
                  type="button"
                  className="btn btn-link text-primary text-decoration-none p-0 border-0"
                  style={{ width: 24, height: 24 }}
                  aria-label="Undo dismiss"
                  onClick={() => onUndismiss(claim, line)}
                >
                  <i className="fa-solid fa-rotate-left" />
                </button>
              </OverlayTrigger>
            )}
          </td>
        </tr>
      );
    });

  const cardsFor = (claim) =>
    visibleLines(claim).map((line) => {
      const id = eraLineId(claim, line);
      const actionable = isActionable(line, claim);
      const dismissable = canDismiss(line, claim);
      return (
        <div
          key={id}
          className={`acc-card era-match-card-line ${rowClass(claim, line)}`}
          role={actionable ? "button" : undefined}
          tabIndex={actionable ? 0 : undefined}
          aria-pressed={actionable ? !!isArmed(claim, line) : undefined}
          onClick={activate(claim, line)}
          onKeyDown={activate(claim, line)}
        >
          <div className="acc-card-top">
            <span className="acc-card-id">{line.tariffCode || "Line"}</span>
            <span className="acc-card-amount">{zar.format(line.paidAmount)}</span>
            {dismissable && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {dismissSelection[id]
                      ? "Keep this line on the list"
                      : "Dismiss: take this line off the list without paying it"}
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  className={`btn btn-link text-decoration-none p-0 border-0 ${
                    dismissSelection[id] ? "text-danger" : "text-secondary"
                  }`}
                  style={{ width: 24, height: 24 }}
                  aria-label={`Dismiss line ${line.tariffCode}`}
                  aria-pressed={!!dismissSelection[id]}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleDismissSelect(claim, line);
                  }}
                >
                  <i className="fa-solid fa-eye-slash" />
                </button>
              </OverlayTrigger>
            )}
          </div>
          <div className="acc-card-body">
            <div className="acc-card-field">
              <span className="acc-card-label">Service date</span>
              <span className="acc-card-value">{fmtDate(line.dateOfService)}</span>
            </div>
            <div className="acc-card-field">
              <span className="acc-card-label">Claimed</span>
              <span className="acc-card-value">{zar.format(line.claimedAmount)}</span>
            </div>
            <div className="acc-card-field">
              <span className="acc-card-label">Status</span>
              <span className="acc-card-value">
                <LineBadge line={line} />
              </span>
            </div>
            {isStaged(claim, line) && (
              <Badge bg="primary" className="mt-2">
                <i className="fa-solid fa-check me-1" />
                Confirmed for receipt
              </Badge>
            )}
            {actionable && (
              <Button
                size="sm"
                variant={isArmed(claim, line) ? "light" : "outline-primary"}
                className={`mt-2 ${isArmed(claim, line) ? "text-primary fw-semibold" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onArm(claim, line, e.currentTarget.closest(".acc-card"));
                }}
              >
                {isArmed(claim, line) ? "Matching, tap to change" : "Match this line"}
              </Button>
            )}
            {line.status === "matched" && line.matchId && onUnmatch && (
              <Button
                size="sm"
                variant="outline-secondary"
                className="mt-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onUnmatch(line);
                }}
              >
                <i className="fa-solid fa-rotate-left me-2" />
                Un-match
              </Button>
            )}
            {line.status === "dismissed" && onUndismiss && (
              <Button
                size="sm"
                variant="outline-secondary"
                className="mt-2"
                onClick={(e) => {
                  e.stopPropagation();
                  onUndismiss(claim, line);
                }}
              >
                <i className="fa-solid fa-rotate-left me-2" />
                Undo dismiss
              </Button>
            )}
          </div>
        </div>
      );
    });

  return (
    <>
      <div className="era-match-pane-head">
        <h5 className="text-primary mb-0">Remittance</h5>
        <div className="d-flex align-items-center gap-2">
          <Badge bg="light" text="dark">
            {outstandingCount} to do
          </Badge>
          {matchedCount > 0 && (
            <Form.Check
              type="switch"
              id="era-show-matched"
              label={`Show matched (${matchedCount})`}
              checked={showMatched}
              onChange={onToggleShowMatched}
            />
          )}
          {dismissedCount > 0 && (
            <Form.Check
              type="switch"
              id="era-show-dismissed"
              label={`Show dismissed (${dismissedCount})`}
              checked={showDismissed}
              onChange={onToggleShowDismissed}
            />
          )}
        </div>
      </div>

      {claims.length === 0 && (
        <p className="text-center py-4">
          No remittance to match on this account.
        </p>
      )}

      {claims.map((claim) => {
        const lines = visibleLines(claim);
        if (lines.length === 0) return null;
        return (
          <div key={`${claim.eraLogId}:${claim.schemeClaimId}`} className="mb-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
              <span className="fw-semibold">
                {claim.patientName || "Patient"}
                {onOpenEraDetail ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>View the full ERA detail for this patient</Tooltip>}
                  >
                    <button
                      type="button"
                      className="btn btn-link btn-sm small text-primary text-decoration-none p-0 border-0 ms-2"
                      onClick={() => onOpenEraDetail(claim)}
                    >
                      {claim.medicalAidName} {fmtDate(claim.eraDate)}
                    </button>
                  </OverlayTrigger>
                ) : (
                  <span className="small text-muted ms-2">
                    {claim.medicalAidName} {fmtDate(claim.eraDate)}
                  </span>
                )}
              </span>
              {onDismissClaim && lines.some((l) => canDismiss(l, claim)) && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => onDismissClaim(claim)}
                >
                  <i className="fa-solid fa-eye-slash me-2" />
                  Dismiss
                </Button>
              )}
            </div>

            <div className="d-none d-lg-block">
              <Table bordered hover responsive size="sm">
                <thead className="text-center">
                  <tr>
                    <th className="text-center align-middle text-primary" style={{ width: 100 }}>
                      <span className="visually-hidden">Match</span>
                    </th>
                    <th className="text-center align-middle text-primary">Tariff</th>
                    <th className="text-center align-middle text-primary">Service date</th>
                    <th className="text-center align-middle text-primary acc-list-num">Claimed</th>
                    <th className="text-center align-middle text-primary acc-list-num">Paid</th>
                    <th className="text-center align-middle text-primary">Status</th>
                    <th className="text-center align-middle text-primary" style={{ width: 48 }}>
                      <span className="visually-hidden">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>{rowsFor(claim)}</tbody>
              </Table>
            </div>

            <div className="d-lg-none">{cardsFor(claim)}</div>
          </div>
        );
      })}
    </>
  );
}
